// Single source of truth for the portfolio: the home index, the featured
// project, and every case-study page are rendered from this file.

export type WorkMedia = {
  src: string
  alt: string
  caption?: string
  /** Contain the asset on a plate — for logo lockups and device shots. */
  contain?: boolean
  plate?: string
  ratio?: '21/9' | '16/9' | '4/3' | '3/2' | '1/1' | 'auto'
}

export type BrandColor = {
  hex: string
  name: string
}

export type Capability = {
  title: string
  text: string
}

export type Result = {
  value: string
  label: string
  note: string
}

export type Project = {
  slug: string
  name: string
  client: string
  industry: string
  timeline: string
  services: string
  tags: string[]
  /** Shown under the case-study title and in the featured card. */
  intro: string
  featured?: boolean
  cover: WorkMedia
  /** Card image and plate colour used on the home work grid. */
  thumb: string
  cardBackground: string
  challenge: { heading: string; paragraphs: string[] }
  approach: {
    heading: string
    paragraphs: string[]
    colors: BrandColor[]
    capabilities: Capability[]
  }
  screens: WorkMedia[]
  quote: { text: string; author: string; role: string }
  results: Result[]
  next: string
}

export const projects: Project[] = [
  {
    slug: 'tende-pay',
    name: 'Tende Pay',
    client: 'Tende Pay',
    industry: 'Fintech · Payments',
    timeline: '6 weeks',
    services: 'Brand · Web · UI/UX',
    tags: ['Branding', 'Web', 'UI/UX'],
    featured: true,
    intro:
      'A CBK-licensed fintech building digital payment rails that connect business people across Africa. We shaped the brand, the marketing site, and the product UI around one promise — track and control every coin.',
    cover: {
      src: 'https://cdn.magicpatterns.com/uploads/3j7BPN9jAyodYERgHj7ddA/Screenshot_2026-07-03_at_11.51.16.png',
      alt: 'Tende Pay marketing site — own your spend, grow your business',
      caption: 'Marketing site — desktop',
      ratio: '16/9',
    },
    thumb:
      'https://cdn.magicpatterns.com/uploads/3j7BPN9jAyodYERgHj7ddA/Screenshot_2026-07-03_at_11.51.16.png',
    cardBackground: '#1E2160',
    challenge: {
      heading: 'Fintech has to earn trust in seconds',
      paragraphs: [
        'Kenyan businesses run money across M-Pesa, bank, and cash at once — and most tools give them almost no visibility into where it all goes. Tende Pay set out to fix that, but a payments product only works if people trust it on sight. The brand had to feel as secure and regulated as it actually is, without turning cold or corporate.',
        'Our job was to make something delightful and dependable at the same time — a brand and interface that a finance manager and a shop owner would both feel at home in.',
      ],
    },
    approach: {
      heading: 'One system across brand, site, and app',
      paragraphs: [
        'We built the identity around the "tP" monogram and a confident orange-and-navy pairing — orange for energy and momentum, navy for the security a regulated fintech has to convey. The tone leaned into the promise that Tende builds delightful experiences, not just infrastructure. That system then carried straight into the product: multiple accounts in one place, a live balance you can hide, and expenses you can see the moment they happen.',
      ],
      colors: [
        { hex: '#e8641e', name: 'Orange · energy' },
        { hex: '#1e2160', name: 'Navy · security' },
      ],
      capabilities: [
        {
          title: 'Your money, your way',
          text: 'Tag every transaction, record the reason for each payment, and categorise spending without friction.',
        },
        {
          title: 'Real-time tracking',
          text: 'Monitor every incoming and outgoing payment across M-Pesa and bank, the moment it lands.',
        },
      ],
    },
    screens: [
      {
        src: 'https://cdn.magicpatterns.com/uploads/8tMqhXPNvi6EsYDuKEEuNW/App-1.png',
        alt: 'Tende Pay app dashboard showing account balance and recent expenses',
        caption: 'Product app — iOS & Android',
        contain: true,
        ratio: '3/2',
      },
      {
        src: 'https://cdn.magicpatterns.com/uploads/9vt8WLhbSJQqgjyZ3JArC1/Poster_tp1.png',
        alt: 'Tende Pay campaign poster — we build delightful experiences',
        caption: 'Brand campaign — social',
        ratio: '3/2',
      },
    ],
    quote: {
      text: 'Yetu gave Tende a face people trust in seconds. The brand and the app finally feel like the same product — confident, clear, and genuinely nice to use.',
      author: 'Abel',
      role: 'CEO, Tende Pay',
    },
    results: [
      {
        value: '1×',
        label: 'Unified system',
        note: 'Across brand, web & app',
      },
      { value: '6 wks', label: 'Launch-ready', note: 'From kickoff to delivery' },
      { value: 'CBK', label: 'Licensed', note: 'Regulated, front and centre' },
    ],
    next: 'novacloud',
  },
  {
    slug: 'novacloud',
    name: 'NovaCloud Solutions',
    client: 'NovaCloud Solutions',
    industry: 'Cloud · IT Services',
    timeline: '5 weeks',
    services: 'Brand · Web Design & Dev',
    tags: ['Branding', 'Web Design', 'Development'],
    intro:
      'A cloud services company that needed to look like the future it was selling. We built the identity, the collateral, and a website that turns "cloud solutions partner" into something you can feel — where innovation meets technology.',
    cover: {
      src: 'https://cdn.magicpatterns.com/uploads/9aJeu6Hp81NzZUyDYKPAir/Screenshot_2026-07-03_at_12.12.05.png',
      alt: 'NovaCloud company profile cover — where innovation meets technology',
      caption: 'Company profile — brand cover',
      ratio: '16/9',
    },
    thumb: 'https://cdn.magicpatterns.com/uploads/wExVB9Wo1JJn6vjYjZzBtQ/nova.png',
    cardBackground: '#222052',
    challenge: {
      heading: "Cloud is invisible, the brand can't be",
      paragraphs: [
        'NovaCloud had the engineering to run serious cloud infrastructure, but on paper it looked like every other IT vendor — blue gradients, stock server racks, and a name buyers forgot as soon as they clicked away. In a category where the product is literally invisible, the brand has to carry all the confidence.',
        'They needed an identity that felt innovative and enterprise-ready at once, plus a site that could turn a cold visitor into a "book a call" without a single jargon-filled paragraph.',
      ],
    },
    approach: {
      heading: 'A mark that radiates',
      paragraphs: [
        'The whole system grew out of one idea — a burst radiating outward, standing in for reach, energy, and data moving in every direction. It became the "o" in the wordmark and a motif we could scale across a business card, a cap, or a full-bleed hero. We paired a deep Space Cadet navy with a confident Yellow Green: navy for the trust an infrastructure partner needs, green for momentum. That palette then carried straight into a website built to lead with outcomes, not features.',
      ],
      colors: [
        { hex: '#222052', name: 'Space Cadet · trust' },
        { hex: '#91c640', name: 'Yellow Green · momentum' },
      ],
      capabilities: [
        {
          title: 'One flexible identity',
          text: 'The sunburst and wordmark hold up from a favicon to signage, merch, and pitch decks without redrawing.',
        },
        {
          title: 'Outcome-first site',
          text: 'Designed and built to open with "accelerate your business," then let the services prove it.',
        },
      ],
    },
    screens: [
      {
        src: 'https://cdn.magicpatterns.com/uploads/ejAiF8wSoB8PaWZ8XBvGqP/Landing_Page.png',
        alt: 'NovaCloud website landing page — accelerate your business with NovaCloud Solutions',
        caption: 'Marketing site — landing page',
        ratio: '16/9',
      },
      {
        src: 'https://cdn.magicpatterns.com/uploads/wExVB9Wo1JJn6vjYjZzBtQ/nova.png',
        alt: 'NovaCloud brand collateral — poster, caps and apparel',
        caption: 'Brand collateral — merch & print',
        ratio: '3/2',
      },
      {
        src: 'https://cdn.magicpatterns.com/uploads/x8KNGohUsVwqYiMnUsECdZ/nova_case_study.svg',
        alt: 'NovaCloud wordmark with the radiating sunburst mark',
        caption: 'Wordmark & mark',
        contain: true,
        plate: '#222052',
        ratio: '3/2',
      },
    ],
    quote: {
      text: 'We finally look like the company we are. Yetu gave us a mark people remember and a site that does the selling before we even pick up the phone.',
      author: 'Amos',
      role: 'Co-founder, NovaCloud Solutions',
    },
    results: [
      { value: '1×', label: 'One identity', note: 'Across brand, merch & web' },
      { value: '5 wks', label: 'Launch-ready', note: 'From kickoff to delivery' },
      { value: '100%', label: 'Custom built', note: 'Designed & developed site' },
    ],
    next: 'flowkey',
  },
  {
    slug: 'flowkey',
    name: 'FlowKey Labs',
    client: 'FlowKey Labs',
    industry: 'SaaS · Service Ops',
    timeline: '6 weeks',
    services: 'Identity · Web · UI/UX',
    tags: ['Identity', 'Web', 'UI/UX'],
    intro:
      'An all-in-one platform that helps local service businesses run smoothly — from bookings and payments to staff and client management. We built the identity and the product experience around one line: streamline, track, grow.',
    cover: {
      src: 'https://cdn.magicpatterns.com/uploads/qUvbuX2eVZvg4FTyVpFap9/aa_1.png',
      alt: 'FlowKey shown across laptop, phone and tablet — run your service business smoothly',
      caption: 'One platform — web, tablet & mobile',
      ratio: '16/9',
    },
    thumb: 'https://cdn.magicpatterns.com/uploads/qUvbuX2eVZvg4FTyVpFap9/aa_1.png',
    cardBackground: '#0F1E17',
    challenge: {
      heading: 'Service businesses run on spreadsheets',
      paragraphs: [
        "Local service businesses — studios, clinics, salons, coaches — juggle bookings, payments, staff and client communication across a patchwork of notebooks, WhatsApp threads and spreadsheets. It's slow, error-prone, and it steals the hours owners would rather spend serving clients.",
        'FlowKey wanted to replace all of it with one calm, dependable platform. The brand had to feel modern and trustworthy enough for money to flow through it, while staying approachable for small teams who aren\'t "software people."',
      ],
    },
    approach: {
      heading: 'One confident, green system',
      paragraphs: [
        'We built the identity around a sharp forward-leaning "F" — a mark that reads as motion and momentum. A single, confident green carries the whole system: growth, money in motion, and a fresh take on ops software that usually looks grey and dull. That palette runs straight from the wordmark into the marketing site and the product UI, so a business owner sees the same FlowKey everywhere.',
      ],
      colors: [
        { hex: '#2fa84f', name: 'FlowKey Green · growth' },
        { hex: '#0f1e17', name: 'Ink · clarity' },
      ],
      capabilities: [
        {
          title: 'Everything in one place',
          text: 'Bookings, payments and client communication live together — no more spreadsheet juggling.',
        },
        {
          title: 'Track & grow',
          text: 'A live view of sessions and net income, so owners can focus on serving clients, not admin headaches.',
        },
      ],
    },
    screens: [
      {
        src: 'https://cdn.magicpatterns.com/uploads/hCg5ab4aDmKVZjRejyeoeb/Group_26.png',
        alt: 'FlowKey mobile app showing upcoming sessions and net income',
        caption: 'Product app — bookings & income',
        contain: true,
        ratio: '3/2',
      },
      {
        src: 'https://cdn.magicpatterns.com/uploads/g3xSNcTwxUKYvMqytRpxH8/Frame_2121457531.png',
        alt: 'FlowKey wordmark with the forward-leaning F mark',
        caption: 'Wordmark & mark',
        contain: true,
        plate: '#0f1e17',
        ratio: '3/2',
      },
    ],
    quote: {
      text: 'Yetu turned a pile of spreadsheets into a product our customers actually love. FlowKey finally looks and works like one calm platform.',
      author: 'Doris',
      role: 'CEO, FlowKey Labs',
    },
    results: [
      { value: '1×', label: 'One platform', note: 'Bookings, payments & clients' },
      { value: '6 wks', label: 'Launch-ready', note: 'From kickoff to delivery' },
      { value: '3×', label: 'Devices covered', note: 'Web, tablet & mobile' },
    ],
    next: 'tende-pay',
  },
]

export function getProject(slug: string): Project | undefined {
  return projects.find((p) => p.slug === slug)
}

export const featuredProject = projects.find((p) => p.featured) ?? projects[0]
export const indexProjects = projects.filter((p) => !p.featured)
